import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import Header from '../components/layout/Header';
import { Card } from '../components/ui/card';
import PasskeyManager from '../components/PasskeyManager';
import { Shield, User, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { cn } from '../lib/utils';

type Section = 'account' | 'security';

const LS_ACTIVE_SECTION = 'settingsActiveSection';

// =====================================================================
// iSentiAlert (D16) → SUPERSEDED by v0.4.1+ architectural pivot
//
// The "Browser notifications" toggle is REMOVED. The previous design
// had a 3-state toggle (auto / on / off) where the "auto" state
// followed the iSentiAlert pair state — when iSentiAlert was paired,
// PWA web-push was disabled (because the native iSentiAlert notifications
// covered it).
//
// As of v0.4.1, the iSentiAlert APK has been replaced by iSentinel360 —
// a single APK that IS the PWA (a WebView wrapper at
// https://app.isentinel360.com). There is no longer a separate app to
// pair, so the "double alert" risk is gone. Web-push via the WebView
// is now the PRIMARY (and only) channel for security alerts. Always on.
//
// See PushHandler.tsx for the corresponding change (the
// `effectiveWebPushEnabled()` check and the `isentialertLinked` gating
// are gone).
// =====================================================================

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  // iSentiAlert (D16): lazy-init from localStorage so the user's
  // section choice survives a page reload. After the Settings toggle
  // triggers a reload (to apply the new web-push state), the user
  // lands back on the section they were on (e.g. Security) instead
  // of being bounced to the default Account tab.
  const [section, setSection] = useState<Section>(() => {
    try {
      const stored = localStorage.getItem(LS_ACTIVE_SECTION);
      if (stored === 'account' || stored === 'security') return stored;
    } catch {
      // localStorage may be disabled (e.g., private mode) — fall through
    }
    return 'account';
  });

  // Persist section on change.
  useEffect(() => {
    try {
      localStorage.setItem(LS_ACTIVE_SECTION, section);
    } catch {
      // best-effort
    }
  }, [section]);

  const sections: Array<{ id: Section; label: string; icon: typeof Shield }> = [
    { id: 'account',  label: t('settings.account')  || 'Account',  icon: User },
    { id: 'security', label: t('settings.security') || 'Security', icon: Shield },
  ];

  if (!user) {
    navigate('/login', { replace: true });
    return null;
  }

  return (
    <div className="h-dvh flex flex-col bg-background">
      <Header view="admin" />

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-4 sm:p-6">
          {/* Title row with back button. */}
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-md hover:bg-accent"
              title="Back"
              aria-label="Back"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold">
              {t('settings.title') || 'Settings'}
            </h1>
          </div>

          {/* Mobile: horizontal tabs. Desktop: vertical list in a sidebar. */}
          <div className="lg:flex lg:gap-6">
            <nav className="lg:w-56 lg:flex-shrink-0 mb-4 lg:mb-0">
              {/* Mobile: horizontal scroll. */}
              <div className="flex gap-1 overflow-x-auto pb-1 lg:hidden">
                {sections.map(s => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setSection(s.id)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors",
                        section === s.id
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-muted/80"
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      {s.label}
                    </button>
                  );
                })}
              </div>

              {/* Desktop: vertical list. */}
              <ul className="hidden lg:block space-y-1">
                {sections.map(s => {
                  const Icon = s.icon;
                  return (
                    <li key={s.id}>
                      <button
                        onClick={() => setSection(s.id)}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                          section === s.id
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-accent"
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="flex-1 text-left">{s.label}</span>
                        {section === s.id && <ChevronRight className="w-4 h-4 opacity-70" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Section content. */}
            <main className="flex-1 min-w-0 space-y-4">
              {section === 'account'  && <AccountSection />}
              {section === 'security' && <SecuritySection />}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Account (read-only) ───────────────────────────────────────────
function AccountSection() {
  const { user } = useAuth();
  const [stationName, setStationName] = useState<string | null>(null);
  const [stationLoading, setStationLoading] = useState(false);

  useEffect(() => {
    const civilOrgId = (user as any)?.civil_respondent_id as string | undefined;
    if ((user as any)?.user_type === 'civil_respondent' && civilOrgId) {
      setStationLoading(true);
      api.getCivilRespondent(civilOrgId)
        .then(org => setStationName(org?.division_name || null))
        .catch(err => console.error('[settings] failed to load org division_name:', err?.message))
        .finally(() => setStationLoading(false));
    }
  }, [(user as any)?.user_type, (user as any)?.civil_respondent_id]);

  if (!user) return null;
  const isCivilRespondent = (user as any)?.user_type === 'civil_respondent';
  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-semibold flex items-center gap-2">
        <User className="w-4 h-4" /> Account
      </h2>
      <div className="space-y-2 text-sm">
        <Row label="Name"  value={user.name} />
        <Row label="Email" value={user.email} />
        <Row label="Role"  value={user.role} />
        {isCivilRespondent ? (
          <Row label="Station" value={stationLoading ? '…' : (stationName || '—')} />
        ) : (
          <Row label="Stores" value={`${user.stores?.length ?? 0}`} />
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        To change your name or email, ask your administrator to update it
        from the user list. Self-service profile editing is not available
        by design.
      </p>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b pb-2 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value || '—'}</span>
    </div>
  );
}

// ─── Security ─────────────────────────────────────────────────────
// No "Change password" form: per OPSEC policy, only an Admin / CADMIN /
// SADMIN / SUPERADMIN can reset credentials, and they do it from the
// per-store user list (the 🔑 Reset button). The user never gets a
// self-service password-change affordance.
//
// Passkey support is wired here. The user can register a passkey for
// this device, see all of their registered passkeys, and revoke any
// (e.g. on a lost or sold device).
//
// v0.4.1+: the "Browser notifications" toggle section has been REMOVED.
// Web-push is always on (see PushHandler.tsx for the corresponding change).
function SecuritySection() {
  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <h2 className="font-semibold flex items-center gap-2">
          <Lock className="w-4 h-4" /> Password
        </h2>
        <p className="text-sm text-muted-foreground">
          Your password is managed by your administrator. To change it,
          ask them to reset it from the user list. Self-service password
          change is not available in this application.
        </p>
      </Card>

      <PasskeyManager />
    </div>
  );
}
